import { type BaseComment, type Comment, type UpdateCommentText } from '@soulhx/fs-common';
import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';
import { db, type ItemSyncStatus, type LocalComment } from '../model/db.js';
import { debounce, SYNC_PUSH_DEBOUNCE_MS } from './debounce.js';
import { syncFetch, syncPull } from './syncFetch.js';
import { nextSyncCursor, nowUtcIso, pullSince, toUtcIso } from './timestamps.js';

const commentPath = (itemId: string) => `/comms/comment/${itemId}`;

const isUnsynced = (status: ItemSyncStatus | undefined): boolean => status !== undefined && status !== 'synced';

export const pullComments = async (itemId: string): Promise<void> => {
  const cursor = await db.syncMeta.get(itemId);
  const since = cursor?.lastSyncedAt;
  const path = since ? `${commentPath(itemId)}?since=${encodeURIComponent(pullSince(since))}` : commentPath(itemId);
  const { data: serverComments, horizon } = await syncPull<Comment[]>(path);

  // compared in UTC - see ActionEntrySync's pull
  let maxLastUpdate = since ? toUtcIso(since) : undefined;

  await db.transaction('rw', db.comments, db.syncMeta, async () => {
    for (const serverComment of serverComments) {
      const lastUpdate = toUtcIso(serverComment.lastUpdate);
      if (!maxLastUpdate || lastUpdate > maxLastUpdate) {
        maxLastUpdate = lastUpdate;
      }

      if (serverComment.deleted) {
        await db.comments.delete(serverComment.id);
        continue;
      }

      const local = await db.comments.get(serverComment.id);
      if (local && isUnsynced(local.syncStatus)) continue;
      if (local?.lastUpdate === serverComment.lastUpdate) continue;

      const toStore: LocalComment = { ...serverComment, syncStatus: 'synced' };
      await db.comments.put(toStore);
    }

    // a first (since-less) pull has no stored cursor to floor at: the
    // horizon alone is safe, and gives a server-clock cursor even when the
    // item has no comments yet; only an API without one falls back to the
    // device clock
    const lastSyncedAt = since
      ? nextSyncCursor(since, maxLastUpdate!, horizon)
      : (horizon ?? maxLastUpdate ?? nowUtcIso());
    await db.syncMeta.put({ itemId, lastSyncedAt });
  });
};

const pushCreate = async (local: LocalComment): Promise<void> => {
  const body: BaseComment = { itemId: local.itemId, text: local.text, replyTo: local.replyTo };
  const serverComment = await syncFetch<Comment>({ method: 'POST', path: '/comms/comment/', body });

  await db.transaction('rw', db.comments, async () => {
    await db.comments.delete(local.id);
    await db.comments.put({ ...serverComment, syncStatus: 'synced' });
  });
};

const pushUpdate = async (local: LocalComment): Promise<void> => {
  const body: UpdateCommentText = { id: local.id, text: local.text };
  const serverComment = await syncFetch<Comment>({ method: 'PUT', path: `/comms/comment/${local.id}`, body });

  await db.comments.put({ ...serverComment, syncStatus: 'synced' });
};

const pushDelete = async (local: LocalComment): Promise<void> => {
  await syncFetch<{ success: boolean }>({ method: 'DELETE', path: `/comms/comment/${local.id}` });
  await db.comments.delete(local.id);
};

export const pushPendingComments = async (itemId: string): Promise<void> => {
  const pending = await db.comments
    .where('itemId')
    .equals(itemId)
    .filter((c) => c.syncStatus !== 'synced')
    .toArray();

  for (const local of pending) {
    try {
      switch (local.syncStatus ?? 'pendingCreate') {
        case 'pendingCreate':
          await pushCreate(local);
          break;
        case 'pendingUpdate':
          await pushUpdate(local);
          break;
        case 'pendingDelete':
          await pushDelete(local);
          break;
      }
    } catch (error) {
      // leave the row's syncStatus untouched so it's retried on the next cycle
      // eslint-disable-next-line no-console
      console.error(`comment sync: failed to push comment ${local.id}`, error);
    }
  }
};

/**
 * Pushes any pending local mutations for an itemId, then pulls whatever
 * changed on the server since the last sync. Push-before-pull so local
 * changes reach `synced` before we ask "what's new", minimizing cases
 * where the pull side has to skip an in-flight local row.
 */
export const syncCommentsForItem = async (itemId: string): Promise<void> => {
  await pushPendingComments(itemId);
  await pullComments(itemId);
};

const logSyncError = (itemId: string, error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`comment sync: sync cycle failed for item ${itemId}`, error);
};

const debouncedTriggers = new Map<string, () => void>();

/**
 * Called after a local write, to push it soon rather than waiting for the
 * item's poll interval (see `CommentSyncMiddleware.ts`) to pick it up.
 * Debounced per itemId, so a burst of edits to the same item collapses
 * into one push. Works even if that item isn't actively polling (e.g. its
 * comment thread was already closed) - it just does a one-off push.
 */
const triggerSync = (itemId: string): void => {
  let trigger = debouncedTriggers.get(itemId);
  if (!trigger) {
    trigger = debounce(
      () => syncCommentsForItem(itemId),
      SYNC_PUSH_DEBOUNCE_MS,
      (error) => logSyncError(itemId, error)
    );
    debouncedTriggers.set(itemId, trigger);
  }

  trigger();
};

export const queueLocalCommentCreate = async (
  itemId: string,
  text: string,
  userId: string,
  isAdmin: boolean,
  replyTo?: string
): Promise<string> => {
  const now = DateTime.now().toISO();
  const localComment: LocalComment = {
    id: uuid(),
    itemId,
    text,
    replyTo,
    userId,
    isReadByAdmin: isAdmin,
    commentFromAdmin: isAdmin,
    createDate: now,
    lastUpdate: now,
    deleted: false,
    syncStatus: 'pendingCreate',
  };

  await db.comments.add(localComment);
  triggerSync(itemId);
  return localComment.id;
};

export const queueLocalCommentUpdate = async (id: string, text: string, isAdmin: boolean): Promise<void> => {
  const itemId = await db.transaction('rw', db.comments, async () => {
    const existing = await db.comments.get(id);
    if (!existing || existing.syncStatus === 'pendingDelete') return undefined;

    const nextStatus: ItemSyncStatus = existing.syncStatus === 'pendingCreate' ? 'pendingCreate' : 'pendingUpdate';

    await db.comments.update(id, {
      text,
      lastUpdate: DateTime.now().toISO(),
      syncStatus: nextStatus,
      commentFromAdmin: isAdmin,
      isReadByAdmin: isAdmin,
    });
    return existing.itemId;
  });

  if (itemId) triggerSync(itemId);
};

export const queueLocalCommentDelete = async (id: string): Promise<void> => {
  const itemId = await db.transaction('rw', db.comments, async () => {
    const existing = await db.comments.get(id);
    if (!existing) return undefined;

    if (existing.syncStatus === undefined || existing.syncStatus === 'pendingCreate') {
      // never reached the server - just vanish, no network call
      await db.comments.delete(id);
      return undefined;
    }

    if (existing.syncStatus === 'pendingDelete') return undefined;

    await db.comments.update(id, { deleted: true, syncStatus: 'pendingDelete' });
    return existing.itemId;
  });

  if (itemId) triggerSync(itemId);
};
